import {
  useMutation,
  useQueries,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteOrgNotification,
  getOrgNotifications,
  getPatientById,
  getPatientEnrollmentById,
} from "../dataAccess/apiService";
import { useEffect, useMemo, useState } from "react";
import { useUserProfile } from "./useUser";
import { useBoundStore } from "../stores/useBoundStore";

export function useNotifications() {
  const queryClient = useQueryClient();
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const { userProfileQuery } = useUserProfile();
  const [infiniteQueryEnabled, setInfiniteQueryEnabled] = useState(false);

  // for deleting notifications
  const addNotificationId = useBoundStore((state) => state.addNotificationId);
  const removeNotificationId = useBoundStore(
    (state) => state.removeNotificationId
  );

  const orgNotificationQuery = useQuery({
    queryKey: ["get-org-notifications-initial", selectedOrgId],
    queryFn: () => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getOrgNotifications(selectedOrgId, undefined);
    },
    enabled: !!selectedOrgId && userProfileQuery.data?.role !== "patient",
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      // poll every 1min, stop poll if error
      return query.state.error ? false : 60000;
    },
  });

  const orgNotificationInfiniteQuery = useInfiniteQuery({
    queryKey: ["get-org-notifications", selectedOrgId],
    queryFn: ({ pageParam }) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getOrgNotifications(selectedOrgId, pageParam);
    },
    enabled:
      infiniteQueryEnabled &&
      !!selectedOrgId &&
      userProfileQuery.data?.role !== "patient",
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextCursor;
    },
  });

  // refetch interval for paginated notifications
  useEffect(() => {
    if (
      infiniteQueryEnabled &&
      !!selectedOrgId &&
      userProfileQuery.data?.role !== "patient"
    ) {
      const intervalId = setInterval(() => {
        orgNotificationInfiniteQuery.refetch();
      }, 60000); // every 60 seconds

      return () => clearInterval(intervalId); // cleanup
    }
  }, [infiniteQueryEnabled, selectedOrgId, userProfileQuery.data?.role]);

  // Compute current notifications without state updates
  const currentNotifications = useMemo(() => {
    // If infinite query has data, use that
    if (
      orgNotificationInfiniteQuery.data?.pages &&
      orgNotificationInfiniteQuery.data.pages.length > 0
    ) {
      return orgNotificationInfiniteQuery.data.pages.flatMap(
        (page) => page!.data
      );
    }
    // Otherwise use initial query data (or while infinite query is still loading)
    return orgNotificationQuery.data?.data ?? [];
  }, [orgNotificationQuery.data, orgNotificationInfiniteQuery.data]);

  // extract enrollment IDs from notifications uri
  const extractEnrollmentId = (url: string) => {
    const parts = url.split("/");
    const index = parts.indexOf("enrollments");
    return index !== -1 ? parts[index + 1] : undefined;
  };

  // Some notification_uri values carry a literal "unknown" placeholder
  // instead of a real enrollment UUID (upstream data issue — see
  // useNotifications diagnostic log). These can never resolve, so treat
  // them the same as a missing ID rather than firing a doomed request.
  const isValidEnrollmentId = (id: string | undefined): id is string =>
    !!id && id !== "unknown";

  // list of enrollmentIds w/o dups
  const enrollmentIds = useMemo(() => {
    return [
      ...new Set(
        currentNotifications.map(
          (notif) => extractEnrollmentId(notif.notification_uri) ?? ""
        )
      ),
    ];
  }, [currentNotifications]);

  // fetch patient names for all enrollment IDs
  const patientNameQueries = useQueries({
    queries: enrollmentIds.map((enrollmentId) => ({
      queryKey: ["patient-name", enrollmentId],
      queryFn: async () => {
        if (!isValidEnrollmentId(enrollmentId)) return "N/A";

        try {
          const enrollment = await getPatientEnrollmentById(enrollmentId);
          if (!enrollment.patient_id) return "N/A";
          const patient = await getPatientById(enrollment.patient_id);
          return `${patient.first_name} ${patient.last_name}`;
        } catch (error) {
          // DIAGNOSTIC: surface which notification(s) reference the
          // enrollment that failed to resolve, so the stale record can be
          // tracked down. Remove once the source data issue is fixed.
          const offendingNotifications = currentNotifications.filter(
            (notif) =>
              extractEnrollmentId(notif.notification_uri) === enrollmentId
          );
          console.error(
            "[useNotifications] Failed to resolve patient name for enrollmentId:",
            enrollmentId,
            "\nFrom notification(s):",
            offendingNotifications.map((n) => ({
              notification_id:
                (n as { id?: string; notification_id?: string }).id ??
                (n as { notification_id?: string }).notification_id,
              notification_uri: n.notification_uri,
            })),
            "\nOriginal error:",
            error
          );
          return "N/A";
        }
      },
      enabled: isValidEnrollmentId(enrollmentId),
      staleTime: 5 * 60 * 1000,
    })),
  });

  // check if any patient names are loading
  const isLoadingPatientNames = patientNameQueries.some(
    (query) => query.isLoading
  );

  // create a map of enrollmentId -> patientName
  const patientNamesMap = useMemo(() => {
    return enrollmentIds.reduce(
      (acc, id, index) => {
        if (id) {
          acc[id] = patientNameQueries[index].data || "N/A";
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [enrollmentIds, patientNameQueries]);

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => {
      if (!selectedOrgId || !notificationId) {
        return Promise.reject(new Error("Missing orgId or notificationId"));
      }
      addNotificationId(notificationId);
      return deleteOrgNotification(selectedOrgId, notificationId);
    },
    onSuccess: (data, notificationId) => {
      // Refetch initial query for accurate count
      queryClient.invalidateQueries({
        queryKey: ["get-org-notifications-initial"],
        exact: false,
      });

      if (
        orgNotificationInfiniteQuery.data?.pages &&
        orgNotificationInfiniteQuery.data.pages.length > 0
      ) {
        orgNotificationInfiniteQuery.refetch();
      }
      removeNotificationId(notificationId);
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: ["get-org-notifications-initial"],
        exact: false,
      });
    },
  });

  return {
    orgNotificationQuery,
    orgNotificationInfiniteQuery,
    currentNotifications,
    isLoadingPatientNames,
    patientNamesMap,
    extractEnrollmentId,
    deleteNotificationMutation,
    setInfiniteQueryEnabled,
  };
}
